import { useState, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from '../context/AuthContext';
import { tokens } from '../tokens';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { supabase } from '../lib/supabase';

/* ── Business context — read safely via a wrapper.
   useBusinessAccount reads from BusinessAccountContext. Personal users render
   MessagingPage outside any BusinessAccountProvider, so we can't call the hook
   directly in this component. Instead, MessagingPage tries to import and call
   it; if it throws at import time or the account is null we fall back to the
   personal user path. ── */
import { useBusinessAccountOptional } from '../business-dashboard/useBusinessAccount';

/* ── Fonts — matches NotificationsPage ── */
const FONTS_URL =
  "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap";

if (!document.getElementById("__notif_fonts")) {
  const l = document.createElement("link");
  l.id = "__notif_fonts";
  l.rel = "stylesheet";
  l.href = FONTS_URL;
  document.head.appendChild(l);
}

/* ════════════════════════════════════════════════
   DESIGN TOKENS — matches NotificationsPage palette
════════════════════════════════════════════════ */
const T = {
  green:      "#1e9254",
  greenHover: "#1b7a47",
  greenPress: "#155c36",
  greenTint:  "#eef8f3",
  greenBd:    "#a7d9bc",
  greenFocusBd: "#a7d9bc",
  greenGlow:  "rgba(30,146,84,0.07)",

  bg:         "#F7F8FA",
  white:      "#FFFFFF",
  border:     "#E5E7EB",
  borderFaint:"#F0F2F5",

  text:       "#0f1117",
  textMid:    "#2d3549",
  sub:        "#4a5268",
  faint:      "#8a93a8",
  ghost:      "#b0b8cc",
  stamp:      "#b0b8cc",

  hoverBg:    "#F3F4F6",
  pressBg:    "#ECEEF1",
  pressDeep:  "#E4E6EA",

  eventBg:    "#F4F5F7",
  eventBd:    "#E4E6EA",
  eventText:  "#52606D",
  eventLabel: "#9CA3AF",

  threadBg:       "#F7F8FA",
  bubbleClient:   "#EEEFF3",
  bubbleClientBd: "#E4E5EA",
  bubblePro:      "#FFFFFF",
  deliveryColor:  "#B0B7C3",

  sz: { xs: 11, sm: 12, base: 13, md: 14, lg: 15 },

  serif: "'Instrument Serif', Georgia, serif",
  sans:  "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
  font:  "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",

  tx: "all 0.18s cubic-bezier(.4,0,.2,1)",
};

/* ════════════════════════════════════════════════
   SHARED INTERACTION HOOK
════════════════════════════════════════════════ */
function useBtn() {
  const [h, setH] = useState(false);
  const [p, setP] = useState(false);
  const bind = {
    onMouseEnter: () => setH(true),
    onMouseLeave: () => { setH(false); setP(false); },
    onMouseDown:  () => setP(true),
    onMouseUp:    () => setP(false),
  };
  return { h, p, bind };
}

/* Status pill colors — soft, low-saturation */
const STATUS_META = {
  "Upcoming":    { bg:"#F4F5F7", color:"#52606D", bd:"#DDE0E5" },
  "In Progress": { bg:"#F0FAF4", color:"#1A6B3C", bd:"#B8DFC4" },
  "Completed":   { bg:"#F4F5F6", color:"#6B7280", bd:"#D8DBE0" },
  "Cancelled":   { bg:"#FEF2F2", color:"#9B2C2C", bd:"#FECACA" },
};

/* ════════════════════════════════════════════════
   ATOMS
════════════════════════════════════════════════ */
function Ava({ pro, size = 40 }) {
  if (pro.avatar_url) {
    return (
      <img
        src={pro.avatar_url}
        alt={pro.name}
        style={{
          width: size, height: size, borderRadius: "50%", flexShrink: 0,
          objectFit: "cover", border: "1.5px solid #E1E3E7",
        }}
      />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%", flexShrink: 0,
      background: "#EDEEF0", border: "1.5px solid #E1E3E7",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: Math.round(size * 0.29), fontWeight: 600,
      color: "#52606D", fontFamily: T.font, letterSpacing: "0.01em",
      userSelect: "none",
    }}>{pro.initials}</div>
  );
}

function ClientAva({ size = 26 }) {
  const { user } = useAuth();
  const userInitials = user?.name
    ? user.name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()
    : "U";
  if (user?.avatar_url) {
    return (
      <img
        src={user.avatar_url}
        alt="You"
        style={{
          width: size, height: size, borderRadius: "50%", flexShrink: 0,
          objectFit: "cover", border: "1.5px solid #D1D5DB",
        }}
      />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%", flexShrink: 0,
      background: "#E5E7EB", border: "1.5px solid #D1D5DB",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: Math.round(size * 0.29), fontWeight: 600,
      color: "#52606D", fontFamily: T.font, letterSpacing: "0.01em",
      userSelect: "none",
    }}>{userInitials}</div>
  );
}

function StatusPill({ status }) {
  const m = STATUS_META[status] || STATUS_META["Upcoming"];
  return (
    <span style={{
      fontSize: T.sz.sm, fontWeight: 500,
      color: m.color, background: m.bg,
      border: `1px solid ${m.bd}`,
      borderRadius: 999, padding: "2px 9px",
      fontFamily: T.font, whiteSpace: "nowrap",
      lineHeight: "18px",
    }}>{status}</span>
  );
}

function Icon({ d, size = 16, stroke = T.sub, strokeWidth = 1.6 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={stroke} strokeWidth={strokeWidth}
      strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      {Array.isArray(d) ? d.map((pt, i) => <path key={i} d={pt}/>) : <path d={d}/>}
    </svg>
  );
}

/* Two-circle brand mark, matching AppLogo.jsx's icon exactly (same
   viewBox and fill colors — do not use the mint/green from any
   reference image, only these two hex values). */
function BrandMark({ size = 44, faded = false }) {
  const w = size * (87 / 52);
  return (
    <svg width={w} height={size} viewBox="0 0 87 52" aria-hidden="true" focusable="false"
      style={{ opacity: faded ? 0.35 : 1, overflow: "visible", display: "block" }}>
      <circle cx="26" cy="26" r="26" fill="#1e9254" />
      <circle cx="61" cy="26" r="26" fill="#15703a" />
    </svg>
  );
}

function Divider() {
  return <div style={{ height: 1, background: T.borderFaint }}/>;
}

/* ════════════════════════════════════════════════
   SYSTEM EVENT — booking/job lifecycle cards
════════════════════════════════════════════════ */
const EVENT_META = {
  booking_confirmed: { icon: "M9 12l2 2 4-4M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z", color: "#1A6B3C", bg: "#F0FAF4", bd: "#B8DFC4" },
  job_started:       { icon: "M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z", color: "#374151", bg: T.eventBg, bd: T.eventBd },
  job_completed:     { icon: "M22 11.08V12a10 10 0 1 1-5.93-9.14M22 4L12 14.01l-3-3", color: "#1A6B3C", bg: "#F0FAF4", bd: "#B8DFC4" },
  payment_sent:      { icon: "M1 4h22v16H1zM1 10h22", color: "#374151", bg: T.eventBg, bd: T.eventBd },
};

function SystemEvent({ msg }) {
  const meta = EVENT_META[msg.type] || EVENT_META.job_started;
  return (
    <div style={{ display: "flex", justifyContent: "center", margin: "20px 0" }}>
      <div style={{
        display: "inline-flex", alignItems: "center", gap: 8,
        background: meta.bg, border: `1px solid ${meta.bd}`,
        borderRadius: 10, padding: "7px 14px", maxWidth: "72%",
      }}>
        <div style={{
          width: 20, height: 20, borderRadius: "50%",
          background: "rgba(255,255,255,0.7)",
          border: `1px solid ${meta.bd}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}>
          <Icon d={meta.icon} size={11} stroke={meta.color} strokeWidth={2}/>
        </div>
        <div style={{ minWidth: 0 }}>
          <span style={{ fontSize: T.sz.sm, fontWeight: 600, color: meta.color, fontFamily: T.font, letterSpacing: "-0.01em" }}>
            {msg.label}
          </span>
          {msg.detail && (
            <span style={{ fontSize: T.sz.sm, color: T.eventLabel, fontFamily: T.font, fontWeight: 400, marginLeft: 6 }}>
              {msg.detail}
            </span>
          )}
        </div>
        <span style={{ fontSize: T.sz.xs, color: T.stamp, fontFamily: T.font, fontWeight: 400, marginLeft: 4, flexShrink: 0, whiteSpace: "nowrap" }}>
          {msg.time}
        </span>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════
   NAV ICON BUTTON
════════════════════════════════════════════════ */
function NavIconBtn({ icon, active, onClick, badge }) {
  const { h, p, bind } = useBtn();
  const { isMobile } = useBreakpoint();

  const bg = active
    ? "rgba(30,146,84,0.10)"
    : p ? T.pressBg
    : h ? T.hoverBg
    : "transparent";

  const iconStroke = active ? T.green : p ? T.sub : h ? "#9CA3AF" : "#B0B7C3";

  return (
    <button
      {...bind}
      onClick={onClick}
      style={{
        width: isMobile ? 36 : 40, height: isMobile ? 36 : 40, borderRadius: 10,
        border: "none", outline: "none",
        background: bg,
        display: "flex", alignItems: "center", justifyContent: "center",
        cursor: "pointer",
        transition: T.tx,
        transform: p && !active ? "scale(0.92)" : "scale(1)",
        minHeight: isMobile ? tokens.spacing[44] : "auto",
        position: "relative",
      }}>
      <Icon d={icon} size={isMobile ? 16 : 18} stroke={iconStroke} strokeWidth={active ? 1.8 : 1.5}/>
      {badge > 0 && (
        <span style={{
          position: "absolute", top: 6, right: 6,
          width: 7, height: 7, borderRadius: "50%",
          background: T.green, border: "1.5px solid #F7F8FA",
        }} />
      )}
    </button>
  );
}

/* ════════════════════════════════════════════════
   FAR LEFT — Icon sidebar (desktop only — see
   MobileBottomNav for the mobile equivalent)
════════════════════════════════════════════════ */
function IconSidebar({ onNav }) {
  const { logoutUser, user } = useAuth();
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();
  const sidebarWidth = isMobile ? 64 : 72;
  const logo = useBtn();

  const isPro = user?.role === 'pro';

  // No "messages" entry — this page IS messages, so it doesn't link to itself.
  // Pros don't get Home or Bookings; customers don't get Earnings.
  const items = isPro
    ? [
        { id: "earnings",      icon: ["M1 4h22v16H1z", "M1 10h22"], path: "/earnings" },
        { id: "notifications", icon: ["M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9", "M13.73 21a2 2 0 0 1-3.46 0"], path: "/notifications" },
        { id: "help",          icon: ["M9.09 9a3 3 0 1 1 5.83 1c0 2-3 3-3 3", "M12 17h.01", "M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z"], path: "/help" },
      ]
    : [
        { id: "home",          icon: ["M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z", "M9 22V12h6v10"], path: "/" },
        { id: "bookings",      icon: ["M8 2v4", "M16 2v4", "M3 10h18", "M3 6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6z"], path: "/bookings" },
        { id: "notifications", icon: ["M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9", "M13.73 21a2 2 0 0 1-3.46 0"], path: "/notifications" },
        { id: "help",          icon: ["M9.09 9a3 3 0 1 1 5.83 1c0 2-3 3-3 3", "M12 17h.01", "M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z"], path: "/help" },
      ];

  const handleLogout = () => {
    logoutUser?.();
    navigate('/');
  };

  return (
    <div style={{
      width: sidebarWidth, flexShrink: 0, background: T.bg,
      borderRight: `1px solid ${T.border}`,
      display: "flex", flexDirection: "column", alignItems: "center",
    }}>
      {/* Logo — icon only, no wordmark, no divider (matches reference) */}
      <div
        {...logo.bind}
        onClick={() => onNav(isPro ? "/pro/dashboard" : "/")}
        role="button"
        tabIndex={0}
        onKeyDown={e => e.key === "Enter" && onNav(isPro ? "/pro/dashboard" : "/")}
        style={{
          height: isMobile ? 52 : 60, width: "100%",
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer",
          background: logo.p ? T.pressBg : logo.h ? T.hoverBg : "transparent",
          transition: T.tx,
          outline: "none",
          padding: "0 8px",
        }}>
        <BrandMark size={isMobile ? 22 : 26} />
      </div>

      {/* Nav icons — tighter vertical rhythm, closer to the logo above */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: isMobile ? 1 : 2, padding: isMobile ? "6px 0" : "6px 0" }}>
        {items.map(item => (
          <NavIconBtn
            key={item.id}
            icon={item.icon}
            active={false}
            onClick={() => onNav(item.path)}
          />
        ))}
      </div>

      <div style={{ paddingBottom: 16, display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
        <NavIconBtn
          icon="M18 20a6 6 0 0 0-12 0M12 14a4 4 0 1 0 0-8 4 4 0 0 0 0 8z"
          onClick={() => onNav(isPro ? "/pro/dashboard" : "/account")}
        />
        {/* Logout reads as a distinct, deliberate action: red on hover, like the
            other destructive action in this file (ActionBtn's "danger" variant). */}
        <button
          onClick={handleLogout}
          title="Log out"
          style={{
            width: isMobile ? 36 : 40, height: isMobile ? 36 : 40, borderRadius: 10,
            border: "none", background: "transparent",
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", transition: T.tx, color: T.faint,
            minHeight: isMobile ? tokens.spacing[44] : "auto",
          }}
          onMouseEnter={e => { e.currentTarget.style.background = "#FEF2F2"; e.currentTarget.style.color = "#C0392B"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = T.faint; }}
        >
          <svg width={isMobile ? 16 : 18} height={isMobile ? 16 : 18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
            <polyline points="16 17 21 12 16 7"/>
            <line x1="21" y1="12" x2="9" y2="12"/>
          </svg>
        </button>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════
   MOBILE BOTTOM NAV — replaces the icon sidebar on
   small screens. Only shown on the conversation-list
   screen; hidden once a thread is open full-screen,
   matching Uber/Bolt-style navigation patterns.
════════════════════════════════════════════════ */
function MobileTabIcon({ item, active, onClick }) {
  const [p, setP] = useState(false);
  return (
    <button
      onClick={onClick}
      onTouchStart={() => setP(true)}
      onTouchEnd={() => setP(false)}
      onMouseDown={() => setP(true)}
      onMouseUp={() => setP(false)}
      onMouseLeave={() => setP(false)}
      style={{
        flex: 1, border: "none", background: "transparent",
        display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "center", gap: 3,
        padding: "6px 0 4px",
        cursor: "pointer", outline: "none",
        WebkitTapHighlightColor: "transparent",
        transform: p ? "scale(0.90)" : "scale(1)",
        transition: "transform 0.15s cubic-bezier(.4,0,.2,1)",
        minHeight: 44,
      }}>
      <div style={{
        width: 30, height: 26, borderRadius: 9,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: active ? "rgba(30,146,84,0.10)" : "transparent",
        transition: T.tx,
      }}>
        <Icon d={item.icon} size={17} stroke={active ? T.green : "#AEB4C2"} strokeWidth={active ? 1.9 : 1.5}/>
      </div>
      <span style={{
        fontSize: 10, fontWeight: active ? 600 : 500,
        color: active ? T.green : "#AEB4C2",
        fontFamily: T.font, letterSpacing: "0.01em",
      }}>{item.label}</span>
    </button>
  );
}

function MobileBottomNav({ onNav }) {
  const { user } = useAuth();
  const isPro = user?.role === 'pro';

  const items = isPro
    ? [
        { id: "earnings",      label: "Earnings",      icon: ["M1 4h22v16H1z", "M1 10h22"], path: "/earnings" },
        { id: "notifications", label: "Alerts",        icon: ["M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9", "M13.73 21a2 2 0 0 1-3.46 0"], path: "/notifications" },
        { id: "help",          label: "Help",          icon: ["M9.09 9a3 3 0 1 1 5.83 1c0 2-3 3-3 3", "M12 17h.01", "M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z"], path: "/help" },
        { id: "account",       label: "Profile",       icon: "M18 20a6 6 0 0 0-12 0M12 14a4 4 0 1 0 0-8 4 4 0 0 0 0 8z", path: "/pro/dashboard" },
      ]
    : [
        { id: "home",          label: "Home",          icon: ["M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z", "M9 22V12h6v10"], path: "/" },
        { id: "bookings",      label: "Bookings",      icon: ["M8 2v4", "M16 2v4", "M3 10h18", "M3 6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6z"], path: "/bookings" },
        { id: "notifications", label: "Alerts",        icon: ["M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9", "M13.73 21a2 2 0 0 1-3.46 0"], path: "/notifications" },
        { id: "help",          label: "Help",          icon: ["M9.09 9a3 3 0 1 1 5.83 1c0 2-3 3-3 3", "M12 17h.01", "M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z"], path: "/help" },
        { id: "account",       label: "Account",       icon: "M18 20a6 6 0 0 0-12 0M12 14a4 4 0 1 0 0-8 4 4 0 0 0 0 8z", path: "/account" },
      ];

  return (
    <div style={{
      flexShrink: 0, background: T.white,
      borderTop: `1px solid ${T.border}`,
      display: "flex", alignItems: "stretch",
      paddingBottom: "env(safe-area-inset-bottom, 0px)",
      boxShadow: "0 -2px 12px rgba(15,17,23,0.04)",
    }}>
      {items.map(item => (
        <MobileTabIcon
          key={item.id}
          item={item}
          active={false}
          onClick={() => onNav(item.path)}
        />
      ))}
    </div>
  );
}

/* ════════════════════════════════════════════════
   TAB BUTTON — pill-style segmented control
════════════════════════════════════════════════ */
function TabBtn({ label, active, onClick }) {
  const { h, p, bind } = useBtn();
  const { isMobile } = useBreakpoint();
  return (
    <button
      {...bind}
      onClick={onClick}
      style={{
        flex: 1,
        padding: isMobile ? "7px 0" : "7px 0",
        border: "none",
        borderRadius: 8,
        background: active
          ? T.green
          : p ? "rgba(15,17,23,0.05)"
          : h ? "rgba(15,17,23,0.03)"
          : "transparent",
        boxShadow: active ? "0 2px 8px rgba(30,146,84,0.25)" : "none",
        fontSize: isMobile ? T.sz.xs : T.sz.sm,
        fontWeight: active ? 600 : 500,
        color: active ? "#fff" : p ? T.textMid : h ? T.sub : T.faint,
        cursor: "pointer", fontFamily: T.font,
        textTransform: "capitalize",
        transition: T.tx,
        outline: "none",
        minHeight: isMobile ? 32 : "auto",
      }}>
      {label}
    </button>
  );
}

/* ════════════════════════════════════════════════
   CONVERSATION ROW
════════════════════════════════════════════════ */
function ConvItem({ conv, active, onClick }) {
  const { h, p, bind } = useBtn();
  const { isMobile } = useBreakpoint();

  const bg = active
    ? "#F5FAF7"
    : p ? T.pressDeep
    : h ? "#F7F8FA"
    : T.white;

  return (
    <div
      {...bind}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => (e.key === "Enter" || e.key === " ") && onClick()}
      style={{
        padding: isMobile ? "13px 16px" : "12px 16px",
        background: bg,
        borderLeft: isMobile ? "none" : `2px solid ${active ? T.green : "transparent"}`,
        borderBottom: `1px solid ${T.borderFaint}`,
        cursor: "pointer",
        transition: T.tx,
        display: "flex", gap: isMobile ? 12 : 11, alignItems: "flex-start",
        outline: "none",
        transform: p && !active ? "scale(0.995)" : "scale(1)",
        transformOrigin: "center left",
        minHeight: isMobile ? tokens.spacing[44] : "auto",
        WebkitTapHighlightColor: "transparent",
      }}>
      <Ava pro={conv.pro} size={isMobile ? 46 : 36}/>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 2 }}>
          <span style={{
            fontSize: isMobile ? T.sz.md : T.sz.md, lineHeight: "19px",
            fontWeight: active || conv.unread > 0 ? 600 : 500,
            color: T.text, fontFamily: T.font,
            letterSpacing: "-0.01em",
          }}>
            {conv.pro.name}
          </span>
          <span style={{ fontSize: T.sz.xs, color: T.stamp, fontFamily: T.font, fontWeight: 400, flexShrink: 0, marginLeft: 8 }}>{conv.time}</span>
        </div>
        <p style={{ margin: "0 0 4px", fontSize: isMobile ? T.sz.xs : T.sz.sm, color: T.faint, fontFamily: T.font, fontWeight: 400, lineHeight: "16px" }}>{conv.pro.service}</p>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6 }}>
          <p style={{
            margin: 0,
            fontSize: isMobile ? T.sz.sm : T.sz.sm, lineHeight: "17px",
            color: conv.unread > 0 ? T.sub : T.ghost,
            fontFamily: T.font, fontWeight: 400,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {conv.lastMsg}
          </p>
          {conv.unread > 0 && (
            <span style={{
              minWidth: 17, height: 17, borderRadius: 99,
              background: T.green, color: "#fff",
              fontSize: T.sz.xs, fontWeight: 600,
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0, fontFamily: T.font, padding: "0 4px",
            }}>
              {conv.unread}
            </span>
          )}
        </div>
      </div>
      {isMobile && (
        <div style={{ alignSelf: "center", flexShrink: 0, opacity: 0.5 }}>
          <Icon d="M9 18l6-6-6-6" size={14} stroke={T.ghost} strokeWidth={2}/>
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════
   LEFT PANEL — Conversation list (real data only)
════════════════════════════════════════════════ */
function ConversationList({ conversations, selected, onSelect, loading }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPro = user?.role === 'pro';
  const [search, setSearch]   = useState("");
  const [tab, setTab]         = useState("all");
  const [focused, setFocused] = useState(false);
  const { isMobile } = useBreakpoint();
  const tabs = ["all", "active", "completed"];

  const filtered = conversations.filter(c => {
    const matchTab    = tab === "all" || c.status === tab;
    const matchSearch = !search
      || c.pro.name.toLowerCase().includes(search.toLowerCase())
      || c.pro.service.toLowerCase().includes(search.toLowerCase());
    return matchTab && matchSearch;
  });

  const panelWidth = isMobile ? "100%" : 320;

  return (
    <div style={{ width: panelWidth, flexShrink: 0, background: T.white, borderRight: isMobile ? "none" : `1px solid ${T.border}`, display: "flex", flexDirection: "column", overflow: "hidden", height: "100%" }}>
      <div style={{
        padding: isMobile ? "calc(env(safe-area-inset-top, 0px) + 14px) 16px 0" : "18px 16px 0",
        flexShrink: 0,
      }}>
        <h2 style={{
          margin: "0 0 14px",
          fontFamily: T.serif,
          fontSize: isMobile ? 26 : 23, fontWeight: 400,
          color: T.text,
          letterSpacing: "-0.02em",
        }}>Messages</h2>

        {/* Search */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, background: focused ? T.white : "#F4F5F7", border: `1.5px solid ${focused ? T.greenFocusBd : "transparent"}`, borderRadius: isMobile ? 12 : 9, padding: "0 12px", height: isMobile ? 42 : 36, boxShadow: focused ? `0 0 0 3px ${T.greenGlow}` : "none", transition: T.tx, marginBottom: 14 }}>
          <Icon d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" size={isMobile ? 14 : 13} stroke={focused ? T.green : "#B8BCC4"}/>
          <input value={search} onChange={e => setSearch(e.target.value)} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} placeholder="Search conversations…" style={{ border: "none", outline: "none", background: "transparent", fontSize: isMobile ? T.sz.md : T.sz.base, fontFamily: T.font, color: T.text, width: "100%", caretColor: T.green, minHeight: isMobile ? tokens.spacing[44] : "auto" }}/>
        </div>

        {/* Tabs — segmented control with a soft-cornered outer track,
            same treatment on mobile and desktop now. */}
        <div style={{
          display: "flex", gap: 2,
          background: "#F0F1F4",
          borderRadius: 10,
          padding: 3,
          marginBottom: 14,
        }}>
          {tabs.map(t => <TabBtn key={t} label={t} active={tab === t} onClick={() => setTab(t)}/>)}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
        {loading ? (
          <div style={{ padding: 24, textAlign: "center", fontSize: T.sz.sm, color: T.faint, fontFamily: T.font }}>
            Loading conversations…
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "48px 20px", textAlign: "center" }}>
            <div style={{ marginBottom: 18 }}>
              <BrandMark size={44}/>
            </div>
            <p style={{ margin: "0 0 6px", fontSize: T.sz.base, fontWeight: 600, color: T.textMid, fontFamily: T.font }}>
              {conversations.length === 0 ? "No conversations yet" : "No matches"}
            </p>
            <p style={{ margin: "0 0 18px", fontSize: T.sz.sm, color: T.faint, fontFamily: T.font, maxWidth: 220, lineHeight: 1.5 }}>
              {conversations.length === 0
                ? (isPro
                    ? "Messages from customers appear here once you accept a job."
                    : "Messages with your pro appear here once you book a service.")
                : "Try a different search or tab."}
            </p>
            {conversations.length === 0 && (
              <button
                onClick={() => navigate(isPro ? "/how-it-works" : "/search")}
                style={{
                  border: "none", outline: "none", cursor: "pointer",
                  background: T.green, color: "#fff",
                  fontFamily: T.font, fontSize: T.sz.sm, fontWeight: 600,
                  padding: "10px 20px", borderRadius: 999,
                  boxShadow: "0 2px 8px rgba(30,146,84,0.25)",
                }}
              >
                {isPro ? "How to get your first booking" : "Find services"}
              </button>
            )}
          </div>
        ) : (
          filtered.map(c => <ConvItem key={c.id} conv={c} active={selected?.id === c.id} onClick={() => onSelect(c)}/>)
        )}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════
   HEADER ICON BUTTON
════════════════════════════════════════════════ */
function HeaderIconBtn({ d, title, onClick }) {
  const { h, p, bind } = useBtn();
  const { isMobile } = useBreakpoint();
  return (
    <button
      {...bind}
      onClick={onClick}
      title={title}
      style={{
        width: isMobile ? 34 : 30, height: isMobile ? 34 : 30, borderRadius: isMobile ? 10 : 7,
        border: "none", outline: "none",
        background: p ? T.pressBg : h ? T.hoverBg : "transparent",
        display: "flex", alignItems: "center", justifyContent: "center",
        cursor: "pointer", transition: T.tx,
        transform: p ? "scale(0.91)" : "scale(1)",
        minHeight: isMobile ? tokens.spacing[44] : "auto",
        WebkitTapHighlightColor: "transparent",
      }}>
      <Icon d={d} size={isMobile ? 14 : 14} stroke={p ? T.sub : h ? "#9CA3AF" : "#B8BCC4"} strokeWidth={1.7}/>
    </button>
  );
}

/* ════════════════════════════════════════════════
   TYPING INDICATOR
════════════════════════════════════════════════ */
function TypingIndicator({ pro }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 8, marginTop: 12 }}>
      <div style={{ width: 26, flexShrink: 0 }}>
        <Ava pro={pro} size={26}/>
      </div>
      <div style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        background: T.bubblePro, border: `1px solid ${T.border}`,
        borderRadius: 14, padding: "10px 14px",
        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
      }}>
        {[0, 1, 2].map(i => (
          <span key={i} style={{
            width: 5, height: 5, borderRadius: "50%",
            background: "#C4C8D0", display: "inline-block",
            animation: `typingDot 1.3s ease-in-out ${i * 0.18}s infinite`,
          }}/>
        ))}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════
   EMPTY THREAD STATE
════════════════════════════════════════════════ */
function EmptyThread({ pro }) {
  const { isMobile } = useBreakpoint();
  return (
    <div style={{
      flex: 1, display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      padding: isMobile ? "0 20px 40px" : "0 32px 48px",
      gap: 8, textAlign: "center",
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: "50%",
        background: T.hoverBg, border: `1px solid ${T.border}`,
        display: "flex", alignItems: "center", justifyContent: "center",
        marginBottom: 4,
      }}>
        <Icon d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" size={18} stroke={T.faint} strokeWidth={1.5}/>
      </div>
      <p style={{ margin: 0, fontSize: isMobile ? T.sz.sm : T.sz.base, fontWeight: 600, color: T.textMid, fontFamily: T.font }}>
        Start the conversation
      </p>
      <p style={{ margin: 0, fontSize: isMobile ? T.sz.xs : T.sz.sm, color: T.faint, fontFamily: T.font, maxWidth: 220, lineHeight: 1.5 }}>
        Ask {pro.name.split(" ")[0]} about availability, pricing, or job details.
      </p>
    </div>
  );
}

/* ════════════════════════════════════════════════
   BUBBLE ROW
════════════════════════════════════════════════ */
function BubbleRow({ isClient, borderRadius, text }) {
  const [hovered, setHovered] = useState(false);
  const { isMobile } = useBreakpoint();
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: isMobile ? "9px 13px" : "10px 14px",
        borderRadius,
        background:  isClient ? T.bubbleClient : T.bubblePro,
        border:      isClient ? `1px solid ${T.bubbleClientBd}` : `1px solid ${T.border}`,
        boxShadow:   hovered
          ? isClient ? "0 2px 8px rgba(0,0,0,0.07)" : "0 2px 8px rgba(0,0,0,0.08)"
          : isClient ? "none" : "0 1px 2px rgba(0,0,0,0.04)",
        transition: "box-shadow 0.18s ease, background 0.15s ease",
        cursor: "default",
      }}
    >
      <p style={{
        margin: 0,
        fontSize: isMobile ? T.sz.sm : T.sz.md,
        color: T.text, fontFamily: T.font,
        lineHeight: 1.62, fontWeight: 400,
        wordBreak: "break-word", userSelect: "text",
      }}>
        {text}
      </p>
    </div>
  );
}

/* ════════════════════════════════════════════════
   COMPOSER ICON BUTTON
════════════════════════════════════════════════ */
function ComposerIconBtn({ d, title, onClick, disabled }) {
  const { h, p, bind } = useBtn();
  const { isMobile } = useBreakpoint();
  return (
    <button
      {...bind}
      onClick={onClick}
      title={title}
      disabled={disabled}
      style={{
        width: isMobile ? 30 : 28, height: isMobile ? 30 : 28, borderRadius: 6,
        border: "none", outline: "none",
        background: p ? T.pressBg : h ? "#F0F1F3" : "transparent",
        display: "flex", alignItems: "center", justifyContent: "center",
        cursor: disabled ? "default" : "pointer", flexShrink: 0,
        transition: T.tx,
        transform: p ? "scale(0.92)" : "scale(1)",
        minHeight: isMobile ? tokens.spacing[44] : "auto",
        opacity: disabled ? 0.5 : 1,
        WebkitTapHighlightColor: "transparent",
      }}>
      <Icon d={d} size={isMobile ? 14 : 15} stroke={p ? T.sub : h ? "#9CA3AF" : "#B8BCC4"} strokeWidth={1.6}/>
    </button>
  );
}

/* ════════════════════════════════════════════════
   SEND BUTTON
════════════════════════════════════════════════ */
function SendBtn({ active, onClick }) {
  const { h, p, bind } = useBtn();
  const { isMobile } = useBreakpoint();

  const bg     = !active ? T.ghost  : p ? T.greenPress : h ? T.greenHover : T.green;
  const shadow = !active ? "none"   : p ? "none" : h ? "0 2px 8px rgba(30,146,84,0.30)" : "0 1px 5px rgba(30,146,84,0.22)";

  return (
    <button
      {...bind}
      onClick={active ? onClick : undefined}
      title="Send message"
      style={{
        height: isMobile ? 34 : 32, width: isMobile ? 34 : 32, borderRadius: 9,
        border: "none", outline: "none",
        background: bg,
        display: "flex", alignItems: "center", justifyContent: "center",
        cursor: active ? "pointer" : "default",
        flexShrink: 0,
        transition: T.tx,
        boxShadow: shadow,
        transform: p && active ? "scale(0.92)" : "scale(1)",
        minHeight: isMobile ? tokens.spacing[44] : "auto",
        WebkitTapHighlightColor: "transparent",
      }}>
      <svg width={isMobile ? 13 : 13} height={isMobile ? 13 : 13} viewBox="0 0 24 24" fill="none"
        stroke={active ? "#fff" : "#E0E2E6"}
        strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="22" y1="2" x2="11" y2="13"/>
        <polygon points="22 2 15 22 11 13 2 9 22 2"/>
      </svg>
    </button>
  );
}

/* ════════════════════════════════════════════════
   INPUT BAR
════════════════════════════════════════════════ */
function InputBar({ conv, onSend, onImageUpload, uploading, sending }) {
  const [input, setInput] = useState("");
  const [focused, setFocused] = useState(false);
  const { isMobile } = useBreakpoint();
  const hasText = input.trim().length > 0 && !sending;
  const fileInputRef = useRef(null);

  const send = () => {
    if (!hasText) return;
    const text = input.trim();
    setInput("");
    onSend(text);
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) onImageUpload(file);
    e.target.value = '';
  };

  const firstName = conv.pro.name.split(" ")[0];

  return (
    <div style={{
      padding: isMobile ? "8px 12px calc(env(safe-area-inset-bottom, 0px) + 10px)" : "10px 20px 14px",
      background: T.white,
      borderTop: `1px solid ${T.borderFaint}`,
      flexShrink: 0,
    }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 0,
        background: focused ? T.white : "#F4F5F7",
        border: `1.5px solid ${focused ? T.greenFocusBd : "transparent"}`,
        borderRadius: isMobile ? 14 : 12,
        height: isMobile ? 46 : 46,
        padding: isMobile ? "0 6px 0 4px" : "0 8px 0 6px",
        boxShadow: focused ? `0 0 0 3px ${T.greenGlow}` : "none",
        transition: T.tx,
      }}>

        <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 0 : 1, paddingRight: 4 }}>
          <ComposerIconBtn
            d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"
            title="Attach file"
            disabled={uploading}
          />
          <ComposerIconBtn
            d="M21 19H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h3l2-3h8l2 3h3a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2zM12 17a4 4 0 1 0 0-8 4 4 0 0 0 0 8z"
            title="Send image"
            onClick={handleImageClick}
            disabled={uploading}
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            disabled={uploading}
          />
        </div>

        <div style={{ width: 1, height: isMobile ? 16 : 18, background: T.borderFaint, flexShrink: 0, margin: isMobile ? "0 4px 0 2px" : "0 8px 0 4px" }}/>

        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onKeyDown={e => {
            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
          }}
          placeholder={`Message ${firstName}…`}
          style={{
            flex: 1, border: "none", outline: "none",
            background: "transparent",
            fontSize: isMobile ? T.sz.md : T.sz.base,
            fontFamily: T.font, color: T.text,
            caretColor: T.green, padding: "0 4px",
            lineHeight: "22px",
            minHeight: isMobile ? tokens.spacing[44] : "auto",
          }}
        />

        <SendBtn active={hasText} onClick={send}/>
      </div>

      {!isMobile && (
        <p style={{
          margin: "5px 4px 0", fontSize: T.sz.xs, color: T.stamp,
          fontFamily: T.font, fontWeight: 400, letterSpacing: "0.01em", lineHeight: 1,
        }}>
          Press{" "}
          <kbd style={{
            fontSize: T.sz.xs, color: T.faint, fontFamily: T.font,
            background: T.hoverBg, border: `1px solid ${T.border}`,
            borderRadius: 4, padding: "0 4px", lineHeight: "16px", display: "inline-block",
          }}>Enter</kbd>
          {" "}to send
        </p>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════
   DATA HELPERS — map Supabase rows into UI shape
════════════════════════════════════════════════ */
function initialsOf(name) {
  if (!name) return "JP";
  return name.split(" ").filter(Boolean).map(w => w[0]).join("").slice(0, 2).toUpperCase();
}

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString("en-KE", { hour: "numeric", minute: "2-digit" });
  const diffDays = Math.floor((now - d) / 86400000);
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return d.toLocaleDateString("en-KE", { weekday: "short" });
  return d.toLocaleDateString("en-KE", { month: "short", day: "numeric" });
}

function mapMessageRow(row, myId) {
  const isMine = row.sender_id === myId;
  return {
    id: row.id,
    from: isMine ? "client" : "pro",
    type: row.type === "image" ? "image" : "text",
    text: row.content,
    media_url: row.media_url || null,
    time: formatTime(row.created_at),
    status: row.status === 'read' ? "read" : "sent",
    created_at: row.created_at,
  };
}

/* ════════════════════════════════════════════════
   MAIN CHAT PANEL — wired to Supabase
════════════════════════════════════════════════ */
function ChatPanel({ conv, myId, onBack }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPro = user?.role === 'pro';
  const [messages, setMessages] = useState([]);
  const [isTyping] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const bottomRef = useRef(null);
  const channelRef = useRef(null);
  const { isMobile } = useBreakpoint();

  const scrollToBottom = (behaviour = "smooth") => {
    bottomRef.current?.scrollIntoView({ behavior: behaviour, block: "end" });
  };

  const isNearBottom = () => {
    const el = scrollRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  };

  const markReadInDb = async (conversationId) => {
    if (!conversationId || !myId) return;
    try {
      await supabase
        .from("chat_messages")
        .update({ status: 'read' })
        .eq("conversation_id", conversationId)
        .neq("sender_id", myId)
        .neq("status", 'read');
    } catch (err) {
      console.error("Failed to mark messages read:", err);
    }
  };

  const loadMessages = async (conversationId) => {
    if (!conversationId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("chat_messages")
        .select("*")
        .eq("conversation_id", conversationId)
        .order("created_at", { ascending: true });
      if (error) throw error;
      setMessages((data || []).map(row => mapMessageRow(row, myId)));
      await markReadInDb(conversationId);
    } catch (error) {
      console.error('Error loading messages:', error);
      setMessages([]);
    } finally {
      setLoading(false);
      requestAnimationFrame(() => scrollToBottom("auto"));
    }
  };

  useEffect(() => {
    if (!conv?.id) {
      setMessages([]);
      return;
    }

    loadMessages(conv.id);

    if (channelRef.current) supabase.removeChannel(channelRef.current);

    channelRef.current = supabase
      .channel(`chat-${conv.id}`)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "chat_messages", filter: `conversation_id=eq.${conv.id}` }, payload => {
        setMessages(prev => {
          if (prev.some(m => m.id === payload.new.id)) return prev;
          const mapped = mapMessageRow(payload.new, myId);
          const updated = [...prev, mapped];
          if (isNearBottom()) setTimeout(() => scrollToBottom("smooth"), 80);
          if (payload.new.sender_id !== myId) markReadInDb(conv.id);
          return updated;
        });
      })
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "chat_messages", filter: `conversation_id=eq.${conv.id}` }, payload => {
        setMessages(prev => prev.map(m => m.id === payload.new.id ? mapMessageRow(payload.new, myId) : m));
      })
      .subscribe();

    return () => {
      if (channelRef.current) supabase.removeChannel(channelRef.current);
    };
  }, [conv?.id, myId]);

  const handleSend = async (text) => {
    if (!conv?.id || !text.trim() || !myId) return;

    setSending(true);
    const tempId = `temp-${Date.now()}`;
    const tempMsg = { id: tempId, from: "client", type: "text", text, time: "Now", status: "sending" };
    setMessages(prev => [...prev, tempMsg]);
    scrollToBottom("smooth");

    try {
      const { data, error } = await supabase
        .from("chat_messages")
        .insert({
          conversation_id: conv.id,
          sender_id: myId,
          content: text,
          type: "text",
        })
        .select()
        .single();
      if (error) throw error;
      setMessages(prev => prev.map(m => m.id === tempId ? mapMessageRow(data, myId) : m));
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => prev.map(m => m.id === tempId ? { ...m, status: "error" } : m));
    } finally {
      setSending(false);
    }
  };

  const retrySend = async (failedMsg) => {
    if (!conv?.id || !myId) return;
    setMessages(prev => prev.map(m => m.id === failedMsg.id ? { ...m, status: "sending" } : m));
    try {
      const { data, error } = await supabase
        .from("chat_messages")
        .insert({
          conversation_id: conv.id,
          sender_id: myId,
          content: failedMsg.text,
          type: "text",
        })
        .select()
        .single();
      if (error) throw error;
      setMessages(prev => prev.map(m => m.id === failedMsg.id ? mapMessageRow(data, myId) : m));
    } catch (error) {
      console.error('Retry failed:', error);
      setMessages(prev => prev.map(m => m.id === failedMsg.id ? { ...m, status: "error" } : m));
    }
  };

  const handleImageUpload = async (file) => {
    if (!conv?.id || !myId) return;

    setUploading(true);
    const tempId = `temp-img-${Date.now()}`;
    const tempMsg = { id: tempId, from: "client", type: "image", media_url: null, time: "Now", status: "sending" };
    setMessages(prev => [...prev, tempMsg]);

    try {
      const ext = file.name.split('.').pop();
      const path = `${conv.id}/${Date.now()}.${ext}`;
      const { error: uploadError } = await supabase.storage.from('chat-media').upload(path, file);
      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage.from('chat-media').getPublicUrl(path);
      const mediaUrl = urlData?.publicUrl;

      const { data, error } = await supabase
        .from("chat_messages")
        .insert({
          conversation_id: conv.id,
          sender_id: myId,
          content: null,
          type: "image",
          media_url: mediaUrl,
        })
        .select()
        .single();
      if (error) throw error;

      setMessages(prev => prev.map(m => m.id === tempId ? mapMessageRow(data, myId) : m));
    } catch (error) {
      console.error('Error uploading image:', error);
      setMessages(prev => prev.filter(m => m.id !== tempId));
    } finally {
      setUploading(false);
    }
  };

  if (!conv) return (
    <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", background: T.bg }}>
      <div style={{ textAlign: "center", maxWidth: 280 }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 22 }}>
          <BrandMark size={70} faded/>
        </div>
        <p style={{ margin: "0 0 8px", fontFamily: T.serif, fontStyle: "italic", fontSize: 22, fontWeight: 400, color: T.text }}>
          {isPro ? "Right next to your customers" : "Chat with your pro"}
        </p>
        <p style={{ margin: 0, fontSize: T.sz.sm, color: T.faint, fontFamily: T.font, lineHeight: 1.5 }}>
          {isPro ? "Accept your first job to start chatting here." : "Book a service to start chatting here."}
        </p>
      </div>
    </div>
  );

  const isEmpty = messages.filter(m => m.from !== 'system').length === 0;
  const innerPadding = isMobile ? "20px 16px 8px" : "36px 32px 8px";

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", background: T.bg, minWidth: 0, height: "100%" }}>

      {/* Chat header */}
      <div style={{
        height: isMobile ? "auto" : 58,
        paddingTop: isMobile ? "env(safe-area-inset-top, 0px)" : 0,
        background: T.white, borderBottom: `1px solid ${T.border}`,
        display: "flex", alignItems: "center",
        padding: isMobile ? "env(safe-area-inset-top, 0px) 8px 0 4px" : "0 18px",
        justifyContent: "space-between", flexShrink: 0, gap: 8,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 4 : 10, minWidth: 0, padding: isMobile ? "8px 0" : 0 }}>
          {isMobile && (
            <HeaderIconBtn d="M15 18l-6-6 6-6" title="Back" onClick={onBack}/>
          )}
          <Ava pro={conv.pro} size={isMobile ? 32 : 32}/>
          <div style={{ minWidth: 0 }}>
            <p style={{ margin: 0, fontSize: isMobile ? T.sz.sm : T.sz.md, fontWeight: 600, color: T.text, fontFamily: T.font, lineHeight: "19px", letterSpacing: "-0.02em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{conv.pro.name}</p>
            <p style={{ margin: 0, fontSize: isMobile ? T.sz.xs : T.sz.sm, fontWeight: 400, color: T.faint, fontFamily: T.font, lineHeight: "17px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{conv.pro.service}&nbsp;&middot;&nbsp;{conv.booking.location}</p>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 4 : 4, flexShrink: 0, padding: isMobile ? "8px 4px" : 0 }}>
          <StatusPill status={conv.booking.status}/>
          {!isMobile && (
            <>
              <div style={{ width: 1, height: 14, background: T.border, margin: "0 8px" }}/>
              <HeaderIconBtn d="M4 6h16M4 12h8" title="Details" onClick={() => {}} />
            </>
          )}
        </div>
      </div>

      {/* Message thread */}
      <div
        ref={scrollRef}
        style={{
          flex: 1, overflowY: "auto", overflowX: "hidden",
          background: T.threadBg, display: "flex", flexDirection: "column",
          WebkitOverflowScrolling: "touch", scrollBehavior: "auto",
        }}
      >
        {loading ? (
          <div style={{ textAlign: "center", padding: 40, color: T.faint, fontFamily: T.font, fontSize: T.sz.sm }}>Loading messages…</div>
        ) : isEmpty ? (
          <EmptyThread pro={conv.pro}/>
        ) : (
          <div style={{ width: "100%", maxWidth: 680, margin: "0 auto", padding: innerPadding, display: "flex", flexDirection: "column" }}>
            {messages.filter(m => m.from !== 'system').length > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 28 }}>
                <div style={{ flex: 1, height: 1, background: T.borderFaint }}/>
                <span style={{ fontSize: T.sz.xs, color: T.stamp, fontFamily: T.font, fontWeight: 500, letterSpacing: "0.06em", textTransform: "uppercase", whiteSpace: "nowrap" }}>Conversation</span>
                <div style={{ flex: 1, height: 1, background: T.borderFaint }}/>
              </div>
            )}

            {messages.map((msg, i) => {
              if (msg.from === "system") return <SystemEvent key={msg.id} msg={msg}/>;

              const isClient = msg.from === "client";
              const prevMsg = messages.slice(0, i).filter(m => m.from !== "system").at(-1);
              const nextMsg = messages.slice(i + 1).filter(m => m.from !== "system")[0];
              const prevSame = prevMsg?.from === msg.from;
              const nextSame = nextMsg?.from === msg.from;
              const prevIsSystem = i > 0 && messages[i - 1].from === "system";
              const marginTop = i === 0 ? 0 : prevIsSystem ? 4 : prevSame ? 3 : 18;
              const showName = !isClient && !prevSame;
              const showMeta = !nextSame;

              const deliveryLabel = msg.status === 'read' ? 'Seen' : msg.status === 'sent' ? 'Sent' : null;

              const R = 14, S = 5;
              let borderRadius;
              if (isClient) {
                borderRadius = prevSame && nextSame ? `${R}px ${S}px ${S}px ${R}px`
                  : prevSame ? `${R}px ${S}px ${R}px ${R}px`
                  : nextSame ? `${R}px ${R}px ${S}px ${R}px`
                  : `${R}px`;
              } else {
                borderRadius = prevSame && nextSame ? `${S}px ${R}px ${R}px ${S}px`
                  : prevSame ? `${S}px ${R}px ${R}px ${R}px`
                  : nextSame ? `${R}px ${R}px ${S}px ${S}px`
                  : `${R}px`;
              }

              return (
                <div key={msg.id} style={{ display: "flex", flexDirection: "column", alignItems: isClient ? "flex-end" : "flex-start", marginTop }}>
                  {showName && (
                    <span style={{ fontSize: T.sz.xs, fontWeight: 600, color: T.faint, fontFamily: T.font, marginBottom: 5, marginLeft: 34, letterSpacing: "0.01em", userSelect: "none" }}>
                      {conv.pro.name}
                    </span>
                  )}

                  <div style={{ display: "flex", flexDirection: isClient ? "row-reverse" : "row", alignItems: "flex-end", gap: 8, width: "100%" }}>
                    {!isClient ? (
                      <div style={{ width: 26, flexShrink: 0, marginBottom: showMeta ? 20 : 0 }}>
                        {!prevSame && <Ava pro={conv.pro} size={26}/>}
                      </div>
                    ) : (
                      <div style={{ width: 26, flexShrink: 0, marginBottom: showMeta ? 20 : 0 }}>
                        {!prevSame && <ClientAva size={26}/>}
                      </div>
                    )}

                    <div style={{ maxWidth: isMobile ? "80%" : "64%", minWidth: 0, display: "flex", flexDirection: "column", alignItems: isClient ? "flex-end" : "flex-start" }}>
                      {msg.type === 'image' ? (
                        <div style={{ padding: 4, borderRadius, background: isClient ? T.bubbleClient : T.bubblePro, border: isClient ? `1px solid ${T.bubbleClientBd}` : `1px solid ${T.border}` }}>
                          {msg.media_url ? (
                            <img
                              src={msg.media_url}
                              alt="Shared"
                              style={{ maxWidth: 200, maxHeight: 200, borderRadius: 8, cursor: 'pointer', display: 'block' }}
                              onClick={() => window.open(msg.media_url, '_blank')}
                            />
                          ) : (
                            <div style={{ width: 160, height: 120, borderRadius: 8, background: T.hoverBg }} />
                          )}
                        </div>
                      ) : (
                        <BubbleRow isClient={isClient} borderRadius={borderRadius} text={msg.text}/>
                      )}

                      {showMeta && (
                        <div style={{ display: "flex", alignItems: "center", gap: 5, margin: "4px 2px 0", justifyContent: isClient ? "flex-end" : "flex-start" }}>
                          <span style={{ fontSize: T.sz.xs, color: T.stamp, fontFamily: T.font, fontWeight: 400, letterSpacing: "0.01em", lineHeight: 1, whiteSpace: "nowrap" }}>
                            {msg.time}
                          </span>
                          {deliveryLabel && (
                            <>
                              <span style={{ fontSize: 9, color: T.deliveryColor, fontFamily: T.font, lineHeight: 1, userSelect: "none" }}>·</span>
                              <span style={{ fontSize: T.sz.xs, color: T.deliveryColor, fontFamily: T.font, fontWeight: 400, letterSpacing: "0.01em", lineHeight: 1, whiteSpace: "nowrap" }}>
                                {deliveryLabel}
                              </span>
                            </>
                          )}
                          {msg.status === 'error' && (
                            <button
                              onClick={() => retrySend(msg)}
                              style={{ fontSize: T.sz.xs, color: '#DC2626', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', marginLeft: 4, fontFamily: T.font }}
                            >
                              Retry
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {isTyping && <TypingIndicator pro={conv.pro}/>}
            {uploading && (
              <div style={{ textAlign: 'center', marginTop: 8 }}>
                <span style={{ fontSize: T.sz.xs, color: T.stamp, fontFamily: T.font }}>Uploading image…</span>
              </div>
            )}

            <div ref={bottomRef} style={{ height: 28 }}/>
          </div>
        )}
      </div>

      <InputBar conv={conv} onSend={handleSend} onImageUpload={handleImageUpload} uploading={uploading} sending={sending}/>
    </div>
  );
}

/* ════════════════════════════════════════════════
   ACTION BUTTON — right sidebar booking actions
════════════════════════════════════════════════ */
function ActionBtn({ children, v, icon }) {
  const { h, p, bind } = useBtn();
  const { isMobile } = useBreakpoint();

  const variants = {
    ghost:    { bg: p ? T.pressBg : h ? T.hoverBg : T.white, color: p ? T.text : T.textMid, bd: `1px solid ${p ? "#CDD0D5" : T.border}`, shadow: "none" },
    danger:   { bg: p ? "#FDECEA" : h ? "#FFF5F5" : T.white, color: "#C0392B", bd: `1px solid ${p ? "#F2B8B4" : "#FECACA"}`, shadow: "none" },
    review:   { bg: p ? "#D6EEE0" : h ? T.greenTint : T.white, color: T.green, bd: `1px solid ${p ? "#A8CFBA" : T.greenBd}`, shadow: "none" },
    complete: { bg: p ? T.greenPress : h ? T.greenHover : T.green, color: "#FFFFFF", bd: "none", shadow: p ? "none" : h ? "0 3px 10px rgba(30,146,84,0.32)" : "0 2px 7px rgba(30,146,84,0.20)" },
  };

  const s = variants[v] || variants.ghost;

  return (
    <button
      {...bind}
      style={{
        display: "flex", alignItems: "center", gap: 8, width: "100%",
        background: s.bg, border: s.bd, borderRadius: 8,
        padding: isMobile ? "6px 9px" : "8px 11px", cursor: "pointer",
        fontFamily: T.font, fontSize: isMobile ? T.sz.sm : T.sz.base, fontWeight: 500, color: s.color,
        transition: T.tx, outline: "none",
        boxShadow: s.shadow,
        transform: p ? "scale(0.97)" : "scale(1)",
        minHeight: isMobile ? tokens.spacing[44] : "auto",
      }}>
      <Icon d={icon} size={12} stroke={s.color} strokeWidth={1.8}/>
      {children}
    </button>
  );
}

/* ════════════════════════════════════════════════
   RIGHT SIDEBAR — Detail panel (desktop only)
════════════════════════════════════════════════ */
function DetailPanel({ conv }) {
  const { isMobile } = useBreakpoint();
  if (!conv || isMobile) return null;
  const { booking, pro } = conv;
  const panelWidth = 288;

  return (
    <div style={{ width: panelWidth, flexShrink: 0, background: T.white, borderLeft: `1px solid ${T.border}`, overflowY: "auto", padding: "20px 16px", display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ textAlign: "center", padding: "14px 0 12px" }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 10 }}>
          <Ava pro={pro} size={52}/>
        </div>
        <p style={{ margin: "0 0 3px", fontSize: T.sz.md, fontWeight: 600, color: T.text, fontFamily: T.font, letterSpacing: "-0.02em" }}>{pro.name}</p>
        <p style={{ margin: 0, fontSize: T.sz.sm, color: T.faint, fontFamily: T.font, fontWeight: 400 }}>{pro.service}</p>
      </div>

      <Divider/>

      <DetailCard title="Booking Details">
        <DetailRow icon="M8 2v4M16 2v4M3 10h18M3 6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6z" label="Service" value={booking.service}/>
        <DetailRow icon="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2zM12 6v6l4 2" label="Date & Time" value={`${booking.date}${booking.time && booking.time !== "TBD" ? ` · ${booking.time}` : ""}`}/>
        <DetailRow icon="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0zM12 7a3 3 0 1 0 0 6 3 3 0 0 0 0 6z" label="Location" value={booking.location}/>
        <DetailRow icon="M1 4h22v16H1zM1 10h22" label="Price" value={`Ksh ${booking.price.toLocaleString()}`} accent/>
        <div style={{ marginTop: 8 }}><StatusPill status={booking.status}/></div>
      </DetailCard>

      <DetailCard title="Actions">
        <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
          {booking.status !== "Completed" && booking.status !== "Cancelled" && (
            <ActionBtn v="ghost" icon="M8 2v4M16 2v4M3 10h18M3 6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6z">Reschedule</ActionBtn>
          )}
          {booking.status === "In Progress" && (
            <ActionBtn v="complete" icon="M22 11.08V12a10 10 0 1 1-5.93-9.14M22 4L12 14.01l-3-3">Mark as Complete</ActionBtn>
          )}
          {booking.status !== "Completed" && booking.status !== "Cancelled" && (
            <ActionBtn v="danger" icon="M18 6L6 18M6 6l12 12">Cancel Booking</ActionBtn>
          )}
          {booking.status === "Completed" && (
            <ActionBtn v="review" icon="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z">Leave a Review</ActionBtn>
          )}
        </div>
      </DetailCard>
    </div>
  );
}

function DetailCard({ title, children }) {
  const { isMobile } = useBreakpoint();
  return (
    <div style={{ background: T.white, borderRadius: 12, border: `1px solid ${T.border}`, padding: isMobile ? "12px 12px 10px" : "14px 14px 12px" }}>
      <p style={{ margin: "0 0 12px", fontSize: T.sz.xs, fontWeight: 600, color: T.faint, fontFamily: T.font, textTransform: "uppercase", letterSpacing: "0.06em" }}>{title}</p>
      {children}
    </div>
  );
}

function DetailRow({ icon, label, value, accent }) {
  const { isMobile } = useBreakpoint();
  return (
    <div style={{ display: "flex", gap: 9, alignItems: "flex-start", marginBottom: 11 }}>
      <div style={{ marginTop: 2, flexShrink: 0 }}>
        <Icon d={icon} size={12} stroke={T.ghost}/>
      </div>
      <div style={{ minWidth: 0 }}>
        <p style={{ margin: 0, fontSize: T.sz.xs, color: T.stamp, fontFamily: T.font, fontWeight: 500, letterSpacing: "0.02em", lineHeight: "14px" }}>{label}</p>
        <p style={{ margin: "3px 0 0", fontSize: isMobile ? T.sz.sm : T.sz.base, color: accent ? T.green : T.textMid, fontFamily: T.font, fontWeight: accent ? 600 : 400, lineHeight: "18px" }}>{value}</p>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════
   CONVERSATION FETCHING — Supabase, single schema
   used by both personal users and business accounts.
   recipientId is whichever side ("user" or "business
   account") is logged in; participant rows carry that
   id so the same query shape works for either.
════════════════════════════════════════════════ */
async function fetchProfilesByIds(ids) {
  const uniqueIds = [...new Set((ids || []).filter(Boolean))];
  if (!uniqueIds.length) return {};
  const { data, error } = await supabase.from("profiles").select("id, full_name, avatar_url").in("id", uniqueIds);
  if (error) { console.error("Failed to load profiles:", error); return {}; }
  const map = {};
  (data || []).forEach(p => { map[p.id] = p; });
  return map;
}
async function fetchConversationsForParticipant(participantId) {
  const { data, error } = await supabase
    .from("conversations")
    .select(`
      id,
      updated_at,
      customer_id,
      professional_id,
      job_request:job_requests (
        id, description, location, scheduled_time, requested_scheduled_time, estimated_price, status
      ),
      chat_messages (
        id, content, type, created_at, sender_id, status
      )
    `)
    .or(`customer_id.eq.${participantId},professional_id.eq.${participantId}`)
    .order("updated_at", { ascending: false });

  if (error) {
    console.error("Failed to load conversations:", error);
    return null;
  }

  const rows = data || [];
  const otherIds = rows.map(row => row.customer_id === participantId ? row.professional_id : row.customer_id);
  const profileMap = await fetchProfilesByIds(otherIds);

  return rows.map(row => {
    const otherId = row.customer_id === participantId ? row.professional_id : row.customer_id;
    const profile = profileMap[otherId];
    const proName = profile?.full_name || "JiraniPro";
    const proAvatar = profile?.avatar_url || null;

    const sortedMsgs = [...(row.chat_messages || [])].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );
    const latest = sortedMsgs[0];
    const unread = (row.chat_messages || []).filter(
      m => m.sender_id !== participantId && m.status !== 'read'
    ).length;

    const status = row.job_request?.status === "completed" ? "completed" : "active";
    const jr = row.job_request;
    const when = jr?.scheduled_time || jr?.requested_scheduled_time || null;

    return {
      id: row.id,
      pro: {
        name: proName,
        service: jr?.description || "Service",
        initials: initialsOf(proName),
        avatar_url: proAvatar,
      },
      lastMsg: latest
        ? (latest.type === "image" ? "📷 Photo" : (latest.content || ""))
        : "No messages yet",
      time: formatTime(latest?.created_at || row.updated_at),
      unread,
      status,
      booking: {
        service: jr?.description || "Service",
        date: when ? new Date(when).toLocaleDateString("en-KE", { month: "short", day: "numeric" }) : "TBD",
        time: when ? new Date(when).toLocaleTimeString("en-KE", { hour: "numeric", minute: "2-digit" }) : "TBD",
        location: jr?.location || "Nairobi",
        price: jr?.estimated_price || 0,
        status: jr?.status
          ? jr.status.charAt(0).toUpperCase() + jr.status.slice(1)
          : "Upcoming",
      },
    };
  });
}

/* ════════════════════════════════════════════════
   PAGE SHELL
════════════════════════════════════════════════ */
export default function MessagingPage() {
  const navigate = useNavigate();
  const { conversationId } = useParams();
  const [selected, setSelected] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [loadingConvos, setLoadingConvos] = useState(true);
  const { isLoggedIn, user } = useAuth();
  const { isMobile } = useBreakpoint();

  // Mobile navigates between a full-screen conversation list and a
  // full-screen thread, the way Uber/Bolt-style chat surfaces work —
  // never both stacked in a shrunken column.
  const [mobileScreen, setMobileScreen] = useState("list");

  // Business account context is present only inside the business dashboard
  // provider tree; useBusinessAccountOptional() returns null outside it
  // instead of throwing, so it's safe to call unconditionally here.
  const businessCtx = useBusinessAccountOptional();
  const businessAccount = businessCtx?.account ?? null;

  // Whichever identity is logged in scopes both the conversation list query
  // and the per-message read receipts in ChatPanel.
  const myId = businessAccount?.id ?? user?.id ?? null;

  useEffect(() => {
    if (!isLoggedIn) navigate('/login');
  }, [isLoggedIn, navigate]);

  useEffect(() => {
    if (!myId) {
      setConversations([]);
      setLoadingConvos(false);
      return;
    }

    let active = true;
    setLoadingConvos(true);

    fetchConversationsForParticipant(myId).then(result => {
      if (!active) return;
      const list = result || [];
      setConversations(list);
      setLoadingConvos(false);

      if (!conversationId && list.length > 0 && !isMobile) {
        setSelected(list[0]);
      }
    });

    return () => { active = false; };
  }, [myId]);

  // Auto-select conversation from URL param once conversations are loaded
  useEffect(() => {
    if (conversationId && conversations.length > 0) {
      const found = conversations.find(c => String(c.id) === String(conversationId));
      if (found) {
        setSelected(found);
        if (isMobile) setMobileScreen("chat");
      }
    }
  }, [conversationId, conversations]);

  // Selecting a conversation also pushes the URL to /messages/:id, so
  // deep-linking and back/forward work correctly.
  const handleSelectConversation = (conv) => {
    setSelected(conv);
    if (isMobile) setMobileScreen("chat");
    if (conv.id && String(conv.id) !== String(conversationId)) {
      navigate(`/messages/${conv.id}`, { replace: false });
    }
  };

  const handleBack = () => {
    setMobileScreen("list");
    navigate("/messages", { replace: false });
  };

  const globalStyles = (
    <style>{`
      *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

      @keyframes typingDot {
        0%, 60%, 100% { opacity: 0.25; transform: translateY(0); }
        30%            { opacity: 1;    transform: translateY(-3px); }
      }

      @keyframes slideInFromRight {
        from { transform: translateX(100%); }
        to   { transform: translateX(0); }
      }

      body { font-family: 'DM Sans', -apple-system, sans-serif; }
      input::placeholder { color: #9CA3AF; font-family: 'DM Sans', sans-serif; }
      input:focus { outline: none; }

      button:focus-visible {
        outline: 2px solid rgba(30, 146, 84, 0.40);
        outline-offset: 2px;
      }
      [role="button"]:focus-visible {
        outline: 2px solid rgba(30, 146, 84, 0.35);
        outline-offset: 1px;
        border-radius: 4px;
      }

      button, [role="button"] {
        user-select: none;
        -webkit-user-select: none;
      }

      button, [role="button"] {
        -webkit-tap-highlight-color: transparent;
      }

      ::-webkit-scrollbar { width: 4px; height: 4px; }
      ::-webkit-scrollbar-track { background: transparent; }
      ::-webkit-scrollbar-thumb { background: #E8EAED; border-radius: 99px; }
      ::-webkit-scrollbar-thumb:hover { background: #D4D7DC; }

      input[type=number]::-webkit-outer-spin-button,
      input[type=number]::-webkit-inner-spin-button { -webkit-appearance: none; }

      @media (prefers-reduced-motion: reduce) {
        * { animation-duration: 0.001ms !important; transition-duration: 0.001ms !important; }
      }
    `}</style>
  );

  if (isMobile) {
    return (
      <div style={{ height: "100dvh", display: "flex", flexDirection: "column", background: T.white, fontFamily: T.font, overflow: "hidden" }}>
        {mobileScreen === "list" ? (
          <>
            <ConversationList
              conversations={conversations}
              selected={selected}
              onSelect={handleSelectConversation}
              loading={loadingConvos}
            />
            <MobileBottomNav onNav={navigate}/>
          </>
        ) : (
          <div style={{ flex: 1, minHeight: 0, animation: "slideInFromRight 0.22s cubic-bezier(.4,0,.2,1)" }}>
            <ChatPanel conv={selected} myId={myId} onBack={handleBack}/>
          </div>
        )}
        {globalStyles}
      </div>
    );
  }

  return (
    <div style={{ height: "100vh", display: "flex", background: T.bg, fontFamily: T.font, overflow: "hidden" }}>
      <IconSidebar onNav={navigate}/>
      <ConversationList
        conversations={conversations}
        selected={selected}
        onSelect={handleSelectConversation}
        loading={loadingConvos}
      />
      <ChatPanel conv={selected} myId={myId}/>
      <DetailPanel conv={selected}/>
      {globalStyles}
    </div>
  );
}